import type { StudyResult } from '../types';
import { CheckCircle, XCircle, Clock, Target, TrendingUp, Award } from 'lucide-react';

interface ProgressTrackerProps {
  results: StudyResult[];
  totalCards?: number;
  className?: string;
  showDetails?: boolean;
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  results,
  totalCards,
  className = '',
  showDetails = true,
}) => {
  if (results.length === 0) {
    return (
      <div className={`text-center py-8 text-gray-500 dark:text-gray-400 ${className}`}>
        <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No study data yet. Start a session to track your progress!</p>
      </div>
    );
  }

  const correct = results.filter((r) => r.quality >= 3).length;
  const incorrect = results.length - correct;
  const accuracy = Math.round((correct / results.length) * 100);
  const totalTime = results.reduce((acc, r) => acc + r.timeSpent, 0);
  const averageTime = Math.round(totalTime / results.length);

  const getAvgQuality = () => {
    if (results.length === 0) return 0;
    const total = results.reduce((acc, r) => acc + r.quality, 0);
    return (total / results.length).toFixed(1);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Overview cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
          <div className="w-8 h-8 mx-auto bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-2">
            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{correct}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Correct</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
          <div className="w-8 h-8 mx-auto bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-2">
            <XCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{incorrect}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Incorrect</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
          <div className="w-8 h-8 mx-auto bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-2">
            <TrendingUp className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{accuracy}%</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Accuracy</p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-center">
          <div className="w-8 h-8 mx-auto bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center mb-2">
            <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {Math.round(averageTime / 1000)}s
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Avg Time</p>
        </div>
      </div>

      {/* Progress bar */}
      {totalCards && totalCards > 0 && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-700 dark:text-gray-300 font-medium">
              Progress
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              {results.length} / {totalCards} cards
            </span>
          </div>
          <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500"
              style={{ width: `${(results.length / totalCards) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Quality detail */}
      {showDetails && (
        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Quality Breakdown
            </h4>
          </div>
          <div className="space-y-2">
            {[0, 1, 2, 3, 4, 5].map((quality) => {
              const count = results.filter((r) => r.quality === quality).length;
              const percentage = results.length > 0 ? (count / results.length) * 100 : 0;
              const labels = ['Forgot', 'Hard', 'Medium', 'Good', 'Great', 'Perfect'];
              const colors = [
                'bg-red-500',
                'bg-orange-500',
                'bg-yellow-500',
                'bg-green-400',
                'bg-green-500',
                'bg-emerald-500',
              ];
              return (
                <div key={quality} className="flex items-center gap-3">
                  <span className="w-16 text-xs text-gray-600 dark:text-gray-400 shrink-0">
                    {labels[quality]}
                  </span>
                  <div className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${colors[quality]} rounded-full transition-all duration-500`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="w-8 text-xs text-gray-500 dark:text-gray-400 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
            Average Quality: <span className="font-semibold text-gray-900 dark:text-gray-100">{getAvgQuality()}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProgressTracker;
